import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Drawer, IconButton, LinearProgress,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PublishIcon from '@material-ui/icons/Publish';
import {
  fetchMerchandisingIndexHistory,
  fetchMerchandisingIndexState,
  fetchMerchandisingReindexStatus,
  startMerchandisingReindex,
} from '../state/merchandisingSlice';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { TypedErrorAlert } from './MerchandisingStatus';

const TERMINAL_STATES = new Set(['completed', 'failed', 'cancelled']);

const IndexStatusBar = ({ inline }) => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const feature = useSelector((state) => state.merchandising);
  const [open, setOpen] = useState(false);
  const automaticPoll = useRef({ revision: null, attempts: 0 });
  const initialLoadScope = useRef(null);

  useEffect(() => {
    if (!feature.capability.root) return;
    const scope = `${feature.scopeShopId || ''}:${feature.scopeGeneration || 0}`;
    if (initialLoadScope.current === scope) return;
    initialLoadScope.current = scope;
    if (!feature.indexState.value) dispatch(fetchMerchandisingIndexState({ languageId: null }));
    dispatch(fetchMerchandisingIndexHistory());
  }, [
    dispatch, feature.capability.root, feature.indexState.value,
    feature.scopeGeneration, feature.scopeShopId,
  ]);

  const jobState = feature.reindex.status?.state || feature.reindex.status?.status || null;
  const activeJob = Boolean(feature.reindex.jobId) && !TERMINAL_STATES.has(jobState);
  useEffect(() => {
    if (!activeJob) return undefined;
    const poll = window.setInterval(() => {
      dispatch(fetchMerchandisingReindexStatus({ jobId: feature.reindex.jobId }));
    }, 2000);
    return () => window.clearInterval(poll);
  }, [activeJob, dispatch, feature.reindex.jobId]);

  useEffect(() => {
    if (!TERMINAL_STATES.has(jobState)) return;
    dispatch(fetchMerchandisingIndexState({ languageId: null }));
    dispatch(fetchMerchandisingIndexHistory());
  }, [dispatch, jobState]);

  const stateValue = feature.indexState.value;
  const isDirty = stateValue?.dirty === true || stateValue?.state === 'dirty';
  const remoteBuildActive = stateValue?.state === 'building' && !activeJob;
  const publishing = activeJob || remoteBuildActive;
  const membershipsAwaitingPublication = stateValue?.merchandising_memberships_ready === false
    && (stateValue?.merchandising_memberships_requested === true
      || stateValue?.dirty_reasons?.includes('merchandising_membership_projection_required'));
  const catalogRecoveryRequired = !publishing && stateValue?.catalog_aligned === false;
  const failed = stateValue?.state === 'failed'
    || stateValue?.catalog_mutation_failed === true
    || catalogRecoveryRequired
    || jobState === 'failed'
    || feature.reindex.request.status === 'error'
    || feature.indexState.request.status === 'error';
  const dirtyCount = isDirty ? Math.max(1, stateValue?.dirty_reasons?.length || 0) : 0;
  const canStart = stateValue?.can_start_full_index && !publishing;
  const automaticRevision = stateValue?.index_required_revision
    || stateValue?.config_revision
    || null;
  const automaticWorkPending = !activeJob && (isDirty || remoteBuildActive);
  useEffect(() => {
    if (automaticPoll.current.revision !== automaticRevision) {
      automaticPoll.current = { revision: automaticRevision, attempts: 0 };
    }
    if (!automaticWorkPending || feature.indexState.request.status === 'loading'
      || feature.indexState.request.status === 'error'
      || automaticPoll.current.attempts >= 60) return undefined;
    const delay = automaticPoll.current.attempts < 5 ? 2000 : 5000;
    const timeout = window.setTimeout(() => {
      automaticPoll.current.attempts += 1;
      dispatch(fetchMerchandisingIndexState({ languageId: null }));
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [
    activeJob, automaticRevision, automaticWorkPending, dispatch,
    feature.indexState.request.status,
  ]);
  const pillState = failed ? 'failed' : (publishing ? 'publishing' : (isDirty ? 'pending' : 'complete'));
  const pillLabel = failed
    ? t('merchandising.index.publicationFailed')
    : (publishing
      ? t('merchandising.index.publishing')
      : (isDirty
        ? t('merchandising.index.pendingChanges', { count: dirtyCount })
        : t('merchandising.index.current')));

  return (
    <>
      <div className={inline ? 'mp-inline-publication' : 'mp-production-publication'}>
        <button
          type="button"
          className={`mp-publication-pill mp-publication-pill--${pillState}`}
          data-testid="merchandising-publication-control"
          data-state={pillState}
          onClick={() => setOpen(true)}
        >
          {pillState === 'complete' ? <CheckCircleOutlineIcon aria-hidden="true" /> : <PublishIcon aria-hidden="true" />}
          <span>
            <strong>{pillLabel}</strong>
            <small>{t('merchandising.index.viewPublicationStatus')}</small>
          </span>
        </button>
      </div>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        classes={{ paper: 'mp-publication-drawer' }}
      >
        <div className="mp-drawer-header">
          <div>
            <span>{t('merchandising.workbench.merchandising')}</span>
            <h2>{t('merchandising.index.publicationStatus')}</h2>
          </div>
          <IconButton aria-label={t('merchandising.transfer.close')} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="mp-publication-state">
          <TypedErrorAlert
            error={feature.indexState.request.error || feature.reindex.request.error}
            onRetry={() => dispatch(fetchMerchandisingIndexState({ languageId: null }))}
          />
          <section>
            <span className="mp-state-symbol is-live"><CheckCircleOutlineIcon /></span>
            <div>
              <strong>{t('merchandising.index.immediateChanges')}</strong>
              <p>{t(membershipsAwaitingPublication
                ? 'merchandising.index.immediateMembershipPending'
                : 'merchandising.index.immediateLive')}</p>
            </div>
          </section>
          <section>
            <span className={`mp-state-symbol ${isDirty || publishing ? 'is-pending' : 'is-live'}`}>
              {failed ? <ErrorOutlineIcon /> : (isDirty || publishing ? <PublishIcon /> : <CheckCircleOutlineIcon />)}
            </span>
            <div>
              <strong>{t('merchandising.index.indexChanges')}</strong>
              <p data-testid="merchandising-index-state">
                {failed && (stateValue?.catalog_mutation_failed || catalogRecoveryRequired)
                  ? t('merchandising.index.catalogRecoveryRequired')
                  : (publishing
                  ? t('merchandising.index.publishing')
                  : (isDirty ? t('merchandising.index.waiting') : t('merchandising.index.current')))}
              </p>
            </div>
          </section>
          {publishing && (
            <div className="mp-publication-progress">
              <LinearProgress
                variant={!activeJob || feature.reindex.status?.progress == null
                  ? 'indeterminate' : 'determinate'}
                value={feature.reindex.status?.progress || 0}
                aria-label={t('merchandising.index.publishing')}
              />
              {activeJob && feature.reindex.status?.progress != null && (
                <span>{t('merchandising.index.progress', { progress: feature.reindex.status.progress })}</span>
              )}
            </div>
          )}
          <p className="mp-publication-revision">
            {t('merchandising.index.revision', {
              config: stateValue?.config_revision || '—',
              live: stateValue?.live_index_revision || stateValue?.live_revision || '—',
            })}
          </p>
        </div>

        <div className="mp-drawer-footer">
          {canStart && (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<PublishIcon />}
              onClick={() => dispatch(startMerchandisingReindex())}
            >
              {t(isDirty ? 'merchandising.index.publishIndex' : 'merchandising.index.rebuildIndex')}
            </Button>
          )}
          {activeJob && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => dispatch(fetchMerchandisingReindexStatus({
                jobId: feature.reindex.jobId,
              }))}
            >
              {t('merchandising.index.refreshStatus')}
            </Button>
          )}
          {remoteBuildActive && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => dispatch(fetchMerchandisingIndexState({ languageId: null }))}
            >
              {t('merchandising.index.refreshStatus')}
            </Button>
          )}
        </div>
      </Drawer>
    </>
  );
};

IndexStatusBar.propTypes = { inline: PropTypes.bool };
IndexStatusBar.defaultProps = { inline: false };

export default IndexStatusBar;
